import { Modal } from "react-bootstrap";
import { PencilSquare, ArrowRepeat } from "react-bootstrap-icons";
import { Button } from "./Button";

export type CommitChangesConfirmDialogProps = {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** True when plan is billed by invoice (accounts receivable), false when auto-pay */
  isInvoiced: boolean;
};

export function CommitChangesConfirmDialog({
  show,
  onClose,
  onConfirm,
  isInvoiced,
}: CommitChangesConfirmDialogProps) {
  if (!show) return null;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Confirm save changes</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isInvoiced ? <InvoicedContent /> : <AutoPayContent />}

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" className="btn-main" onClick={onConfirm}>
            Confirm & save
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function InvoicedContent() {
  return (
    <>
      <div className="text-muted small mb-3">
        Here&apos;s how billing will be affected:
      </div>

      <div className="d-flex flex-column gap-3">
        <div className="d-flex gap-3 align-items-start">
          <div
            className="rounded flex-shrink-0 d-flex align-items-center justify-content-center text-white"
            style={{ width: 40, height: 40, backgroundColor: "var(--bs-primary)" }}
          >
            <PencilSquare size={20} />
          </div>
          <div className="flex-grow-1 min-w-0">
            <div className="fw-bold mb-1">Before payment or period starts</div>
            <div className="text-muted small">
              An <b>amended invoice</b> will be sent to the account holder with updated charges.
            </div>
          </div>
        </div>

        <div className="d-flex gap-3 align-items-start">
          <div
            className="rounded flex-shrink-0 d-flex align-items-center justify-content-center text-white"
            style={{ width: 40, height: 40, backgroundColor: "var(--bs-primary)" }}
          >
            <ArrowRepeat size={20} />
          </div>
          <div className="flex-grow-1 min-w-0">
            <div className="fw-bold mb-1">After payment or period started</div>
            <div className="text-muted small">
              Adjustments will appear on the <b>next invoice</b>.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function AutoPayContent() {
  return (
    <>
      <div className="mb-3">
        <div className="text-muted small">
          The changes you've made will affect the account's billing as follows:
        </div>
      </div>

      <div className="fw-semibold mb-2">Billing impact</div>

      <div className="d-flex flex-column gap-2">
        <div className="px-3 py-2 rounded border">
          <div className="fw-medium">Next renewal</div>
          <div className="text-muted small">
            The changes will be reflected in the account holder&apos;s next renewal cycle. Their current period charge is already set; their next automatic charge will include these updates.
          </div>
        </div>
      </div>

      <div className="fw-semibold mb-2 mt-3">Summary</div>
      <ul className="mb-0 small text-muted ps-3">
        <li>Changes apply at the account holder&apos;s next renewal</li>
        <li>Current period is unchanged</li>
      </ul>
    </>
  );
}
